import { fasStar } from 'shared/@generated/icons/fasStar';
import { Image as RNImage, Platform, type ImageSourcePropType, View } from 'react-native';
import { Text } from 'shared/components/text';
import { Trans, useTranslation } from 'react-i18next';
import { Props } from 'shared/@types';
import { SvgImage } from 'shared/components/svg-image';
import TrustpilotStars from 'shared/assets/brands/trustpilot-stars.svg';
import logoSvg from 'shared/assets/logo.svg';
import logoPng from 'shared/assets/logo.png';
import { Icon } from 'shared/components/icon';

function parseSummary(summary: string, boldClassName = 'font-bold') {
  const parts = summary.split(/(<bold[12]>.*?<\/bold[12]>)/g).filter(Boolean);

  return parts.map((part, index) => {
    const match = part.match(/^<bold[12]>(.*?)<\/bold[12]>$/);

    if (!match) {
      return (
        <Text key={`text-${index}`} skipDefaults>
          {part}
        </Text>
      );
    }

    return (
      <Text key={`bold-${index}`} skipDefaults className={boldClassName}>
        {match[1]}
      </Text>
    );
  });
}

function NativeTrustpilotStars({ compact = false }: { compact?: boolean }) {
  const containerClassName = compact ? 'h-4 gap-0.5' : 'h-6 gap-1';
  const cellClassName = compact ? 'h-4 w-4 rounded-[2px]' : 'h-6 w-6 rounded-[3px]';
  const iconClassName = compact ? 'size-2.5 text-white' : 'size-3.5 text-white';

  return (
    <View className={`flex-row items-center ${containerClassName}`}>
      {[0, 1, 2, 3].map((index) => (
        <View
          key={`trustpilot-star-${index}`}
          className={`${cellClassName} items-center justify-center bg-[#2f8840]`}
        >
          <Icon icon={fasStar} className={iconClassName} />
        </View>
      ))}

      <View
        className={`${cellClassName} relative items-center justify-center overflow-hidden bg-[#efdecd]`}
      >
        <View className={'absolute inset-y-0 left-0 w-1/2 bg-[#2f8840]'} />
        <Icon icon={fasStar} className={iconClassName} />
      </View>
    </View>
  );
}

const ReviewsSummaryCard = ({
  compact = false,
  horizontal = false,
  hero = false,
  inline = false,
  className,
}: Props<{ compact?: boolean; horizontal?: boolean; hero?: boolean; inline?: boolean }>) => {
  const { t, i18n } = useTranslation('components');
  const appleReviews = 902;
  const appleRating = 4.5;
  const androidReviews = 148;
  const androidRating = 4.2;
  const trustpilotReviews = 80;
  const trustpilotRating = 4.6;
  const logo = logoSvg;
  const trustpilotStars = TrustpilotStars;

  const reviews = appleReviews + androidReviews + trustpilotReviews;
  const rating =
    Math.ceil(
      ((appleReviews * appleRating +
        androidReviews * androidRating +
        trustpilotReviews * trustpilotRating) /
        reviews) *
        10,
    ) / 10;
  const summary = t('reviews_card.rating_summary', { rating, reviews });

  if (inline) {
    const numberFormat = new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language);
    const inlineSummary = t('reviews_card.inline_summary', {
      rating: numberFormat.format(rating),
      reviews: numberFormat.format(reviews),
    });

    return (
      <View className={`flex-row items-center gap-2 ${className ?? ''}`}>
        {Platform.OS === 'web' ? (
          <SvgImage
            src={trustpilotStars}
            alt={'Trustpilot Stars'}
            containerClassName={'aspect-[5/1] w-20'}
          />
        ) : (
          <NativeTrustpilotStars compact />
        )}

        <Text className={'text-brand-brown-dark/65 text-xs'}>
          {parseSummary(inlineSummary, 'text-brand-brown-dark font-semibold')}
        </Text>
      </View>
    );
  }

  if (Platform.OS !== 'web') {
    return compact ? (
      <View
        className={
          'border-brand-green/50 flex-row items-center gap-1.5 rounded-md border bg-white px-2 py-1.5'
        }
      >
        <RNImage
          source={logoPng as ImageSourcePropType}
          accessibilityLabel={'Get a Pet Logo'}
          resizeMode={'contain'}
          style={{ width: 52, height: 16 }}
        />

        <NativeTrustpilotStars compact={true} />

        <Text className={'text-xs font-semibold'}>{rating}</Text>
      </View>
    ) : (
      <View
        className={`items-center gap-1.5 rounded-lg ${className} ${horizontal ? 'flex-row items-center p-1.5 md:p-3' : 'p-3'}`}
      >
        <RNImage
          source={logoPng as ImageSourcePropType}
          accessibilityLabel={'Get a Pet Logo'}
          resizeMode={'contain'}
          style={{ width: 80, height: 24 }}
        />

        <NativeTrustpilotStars />

        <Text className={'text-2xs text-brand-brown-dark text-center'}>
          {parseSummary(summary)}
        </Text>
      </View>
    );
  }

  return compact ? (
    <View
      className={
        'border-brand-brown-dark/20 flex-row items-center justify-around space-x-1 rounded border bg-white p-1'
      }
    >
      <SvgImage
        src={trustpilotStars}
        alt={'Trustpilot Stars'}
        containerClassName={'h-4 w-16 sm:w-20'}
      />

      <Text className={'text-xs font-semibold sm:text-xs'}>{rating}</Text>
    </View>
  ) : hero ? (
    <View className={`items-center gap-1.5 ${className}`}>
      <SvgImage src={logo} alt={'Get a Pet Logo'} containerClassName="aspect-logo w-24" />

      <SvgImage
        src={TrustpilotStars}
        alt={'Trustpilot Stars'}
        containerClassName="aspect-[5/1] w-44"
      />

      <Text className="text-brand-brown-dark text-sm md:text-base">
        <Trans
          ns={'components'}
          i18nKey="reviews_card.rating_summary"
          values={{ rating, reviews }}
          components={{
            bold1: <strong />,
            bold2: <strong />,
          }}
        />
      </Text>
    </View>
  ) : (
    <View
      className={`items-center gap-1.5 rounded-lg ${className} ${horizontal ? 'flex-row items-center p-1.5 md:p-3' : 'p-3'}`}
    >
      <SvgImage src={logo} alt={'Get a Pet Logo'} containerClassName={'aspect-logo w-16 md:w-20'} />

      <SvgImage
        src={trustpilotStars}
        alt={'Trustpilot Stars'}
        containerClassName={'aspect-[5/1] w-32 md:w-40'}
      />

      <Text className={'text-2xs text-brand-brown-dark md:text-xs'}>{parseSummary(summary)}</Text>
    </View>
  );
};

export { ReviewsSummaryCard };
